import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  background-color: #fafafa;
  min-height: 100vh;
`;

export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-family: 'Abel', sans-serif;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  a {
    color: ${({ theme }) => theme.colors.text.secondary};
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #657455;
    }
  }

  span {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const GallerySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const MainImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const NavArrow = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ side }) => (side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  color: ${({ theme }) => theme.colors.main};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.2s, transform 0.2s;
  z-index: 2;

  svg {
    width: 22px;
    height: 22px;
  }

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.06);
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }
`;

export const ImageCounter = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-family: 'Abel', sans-serif;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  z-index: 2;
`;

export const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const Thumbnail = styled.button<{ $active?: boolean }>`
  aspect-ratio: 1;
  background-color: white;
  border: 2px solid ${({ $active }) => ($active ? '#657455' : '#e0e0e0')};
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    border-color: #657455;
  }
`;

export const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const ProductTitle = styled.div`
  h1 {
    font-family: 'Abel', sans-serif;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
    color: ${({ theme }) => theme.colors.main};
    margin: 0 0 0.5rem 0;
  }

  .reference {
    font-family: 'Abel', sans-serif;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

export const ProductMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  span {
    &:not(:last-child)::after {
      content: '|';
      margin-left: 0.5rem;
      color: #e0e0e0;
    }
  }

  .condition {
    color: #657455;
    font-weight: 600;
  }
`;

export const PriceSection = styled.div`
  .price {
    font-family: 'Abel', sans-serif;
    font-size: 2.5rem;
    font-weight: 600;
    color: #b8964a;
    margin-bottom: 0.5rem;
  }

  .vat-info {
    font-family: 'Abel', sans-serif;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const PaymentMethods = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;

  .label {
    font-family: 'Abel', sans-serif;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .icon {
    font-size: 1.5rem;
  }
`;

export const PurchaseButton = styled.button`
  width: 100%;
  background-color: #b8964a;
  border: none;
  color: white;
  font-family: 'Abel', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background-color: #a07f3a;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(184, 150, 74, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const CartLinkButton = styled.button`
  display: block;
  width: 100%;
  background-color: white;
  border: 2px solid #b8964a;
  color: #b8964a;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.8rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 1rem;

  &:hover {
    background-color: #b8964a;
    color: white;
  }
`;


export const InquiryButton = styled.button`
  width: 100%;
  background-color: transparent;
  border: 1.5px solid #b8964a;
  color: #b8964a;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  padding: 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-top: 0.75rem;

  &:hover {
    background-color: #b8964a;
    color: #fff;
  }
`;


export const InfoBox = styled.div`
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 1rem 1.5rem;

  .title {
    font-family: 'Abel', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }

  .icon {
    color: #657455;
    font-size: 1.2rem;
    flex-shrink: 0;
  }
`;

export const ContactButton = styled.button`
  width: 100%;
  background-color: white;
  border: 2px solid #b8964a;
  color: #b8964a;
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover {
    background-color: #b8964a;
    color: white;
  }
`;

export const TabsSection = styled.div`
  margin-top: 3rem;
  grid-column: 1 / -1;
`;

export const TabHeader = styled.div`
  display: flex;
  gap: 2rem;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 2rem;
`;

export const Tab = styled.button<{ $active?: boolean }>`
  font-family: 'Abel', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? '#b8964a' : theme.colors.text.secondary)};
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => ($active ? '#b8964a' : 'transparent')};
  padding: 1rem 0;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  &:hover {
    color: #b8964a;
  }
`;

export const TabContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const DetailsColumn = styled.div`
  .title {
    font-family: 'Abel', sans-serif;
    font-size: 1.3rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 1.5rem;
    letter-spacing: 0.5px;
  }
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #e8e8e8;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;

  .label {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 500;
  }

  .value {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 400;
  }
`;

export const Description = styled.div`
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-line;
`;

export const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  span {
    font-family: 'Abel', sans-serif;
    font-size: 1rem;
  }
`;

export const BadgeRow = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
    width: 100%;

    @media (min-width: 601px) {
        gap: 1.75rem;
    }
`;

export const Badge = styled.img<{ src?: any }> `
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    max-width: 110px;
    aspect-ratio: 1;
    object-fit: contain;

    @media (min-width: 601px) {
        flex: 0 0 auto;
        width: 150px;
        max-width: 150px;
    }

    @media (max-width: 600px) {
        max-width: 92px;
    }
`;
